import { selectedFragmentElementAtom, selectedLayersAtom } from '@/atoms';
import type { FragmentPropertyNode } from '@/atoms/fragmentSelection.atom';
import { useAtom, useAtomValue } from 'jotai';
import { ChevronDown, ChevronRight, Crosshair, Loader2 } from 'lucide-react';
import { useState, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ID } from './ID';
import { jumpToFragment } from './IFC2';
import { Button } from './ui/button';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer';
import { Label } from './ui/label';

/**
 * Collapsible property group (relation / related item).
 */
const PropertyGroup = ({ node }: { node: FragmentPropertyNode }) => {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation('components');

  const label = node.type === 'relation' ? node.name : `${t('fragmentProperties.element')} #${node.name}`;

  return (
    <div className='flex flex-col gap-1'>
      <button
        type='button'
        className='flex items-center gap-1 text-sm font-medium text-left hover:opacity-80'
        onClick={() => setOpen((v) => !v)}>
        {open ? <ChevronDown className='size-3.5' /> : <ChevronRight className='size-3.5' />}
        {label}
      </button>
      {open && node.children && (
        <div className='ml-3 flex flex-col gap-1 border-l border-slate-200/70 dark:border-stone-700/70 pl-3'>
          {node.children.map((child, i) => (
            <PropertyNodeView key={`${child.name}-${child.localId}-${i}`} node={child} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Renders a single property node – either an attribute (leaf) or a group.
 */
const PropertyNodeView = ({ node }: { node: FragmentPropertyNode }) => {
  if (node.type === 'relation' || node.type === 'related') {
    return <PropertyGroup node={node} />;
  }

  // Attribute leaf
  return (
    <div className='flex flex-col gap-0.5'>
      <span className='text-xs text-muted-foreground'>{node.name}</span>
      <span className='text-sm break-all'>{String(node.value ?? '')}</span>
    </div>
  );
};

const FragmentProperties: FunctionComponent = () => {
  const { projectId } = useParams();
  const [selected, setSelected] = useAtom(selectedFragmentElementAtom);
  const layers = useAtomValue(selectedLayersAtom(projectId!));
  const layer = layers.find((l) => l.id === selected?.layerId);
  const { t } = useTranslation('components');

  const handleClose = () => setSelected(null);

  return (
    <Drawer open={!!selected} onClose={handleClose}>
      <DrawerContent>
        <DrawerHeader className='w-lg'>
          <DrawerTitle>{t('fragmentProperties.title')}</DrawerTitle>
        </DrawerHeader>
        <div className='flex-1 overflow-y-auto'>
          <div className='flex flex-col gap-4 px-4 py-2'>
            {/* Layer name */}
            <div className='flex flex-col gap-1'>
              <Label>{t('fragmentProperties.layer')}</Label>
              <ID value={layer?.name || selected?.modelId || ''} />
            </div>

            {/* Local ID */}
            <div className='flex flex-col gap-1'>
              <Label>{t('fragmentProperties.localId')}</Label>
              <ID value={String(selected?.localId ?? '')} />
            </div>

            {selected && (
              <Button
                variant='outline'
                size='sm'
                className='self-start'
                onClick={() => jumpToFragment(selected.modelId, selected.localId)}>
                <Crosshair className='size-4' />
                {t('fragmentProperties.jumpTo')}
              </Button>
            )}

            {/* Properties tree */}
            {selected?.loading ? (
              <div className='flex items-center gap-2 text-muted-foreground text-sm'>
                <Loader2 className='size-4 animate-spin' />
                {t('fragmentProperties.loadingProperties')}
              </div>
            ) : selected?.properties && selected.properties.length > 0 ? (
              <div className='flex flex-col gap-3'>
                <Label>{t('fragmentProperties.properties')}</Label>
                {selected.properties.map((node, i) => (
                  <PropertyNodeView key={`${node.name}-${node.localId}-${i}`} node={node} />
                ))}
              </div>
            ) : (
              <p className='text-muted-foreground text-sm'>{t('fragmentProperties.noProperties')}</p>
            )}
          </div>
        </div>
        <DrawerFooter>
          <Button variant='link' onClick={handleClose}>
            {t('fragmentProperties.close')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default FragmentProperties;
